<template>
  <v-dialog :value="value" max-width="920" scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="success white--text py-2 px-4">
        <span class="title pt-1">{{ title }}</span>
        <v-spacer></v-spacer>
        <v-btn class="white--text my-0" icon flat small dark @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4" style="max-height: 75vh">
        <div v-if="loading" class="text-xs-center py-4">
          <v-progress-circular indeterminate color="success"></v-progress-circular>
        </div>

        <v-layout v-else row wrap>
          <v-flex xs12 sm6 class="pr-3">
            <v-card flat class="pa-3 mb-3 deduction-info-card">
              <v-layout row align-center class="mb-2">
                <v-icon color="orange darken-1" small class="mr-2">mdi-currency-usd</v-icon>
                <div class="font-weight-medium">Deduction</div>
                <v-spacer></v-spacer>
              </v-layout>

              <template v-if="preview">
                <div class="caption grey--text mb-2">
                  {{ preview.payAbsentCount }} of
                  {{ preview.payRouteCount }} pay routes on this day
                  <span v-if="preview.category">
                    — {{ categoryLabel(preview.category) }}
                  </span>
                </div>

                <div v-if="preview.existing" class="mb-3 body-2 current-deduction-box">
                  <div class="font-weight-medium">
                    Current: ${{ money(preview.existing.amount) }}
                    ({{ presetLabel(preview.existing.preset) }})
                  </div>
                  <div v-if="preview.existing.enteredBy" class="caption grey--text">
                    Entered by {{ preview.existing.enteredBy }}
                    <span v-if="preview.existing.enteredOn">
                      on {{ preview.existing.enteredOn | time }}
                    </span>
                  </div>
                </div>
                <div v-else class="mb-3 no-deduction-banner">
                  <v-icon color="#c62828" small class="mr-2">mdi-alert-circle</v-icon>
                  <span>No deduction recorded yet for this day</span>
                </div>

                <v-radio-group v-model="form.selectedPreset" hide-details class="mt-0">
                  <v-radio
                    v-for="suggestion in preview.suggestions.filter((s) => s.preset !== 'NO_DEDUCT')"
                    :key="suggestion.preset"
                    :value="suggestion.preset"
                    color="success"
                    :label="suggestion.label + ' — $' + money(suggestion.amount)"
                  ></v-radio>
                  <v-radio value="MANUAL" color="success" label="Custom amount"></v-radio>
                  <v-radio
                    v-for="suggestion in preview.suggestions.filter((s) => s.preset === 'NO_DEDUCT')"
                    :key="suggestion.preset"
                    :value="suggestion.preset"
                    color="success"
                    :label="suggestion.label + ' — $' + money(suggestion.amount)"
                  ></v-radio>
                </v-radio-group>

                <v-text-field
                  v-if="form.selectedPreset === 'MANUAL'"
                  v-model="form.customAmount"
                  label="Amount"
                  type="number"
                  prefix="$"
                  class="mt-1"
                ></v-text-field>
                <v-text-field
                  v-if="form.selectedPreset === 'MANUAL'"
                  v-model="form.customReason"
                  label="Reason (optional)"
                  class="mt-1"
                ></v-text-field>
                <v-text-field
                  v-if="form.selectedPreset === 'NO_DEDUCT'"
                  v-model="form.noDeductReason"
                  label="Reason for no deduct"
                  class="mt-1"
                ></v-text-field>

                <v-layout row class="mt-2">
                  <div class="mr-3">
                    <div class="caption grey--text mb-1">Absent from</div>
                    <DxDateBox
                      type="time"
                      picker-type="list"
                      :interval="15"
                      :width="150"
                      :value="asTimeValue(form.timeFrom)"
                      :show-clear-button="true"
                      @value-changed="onTimeChanged('timeFrom', $event)"
                    />
                  </div>
                  <div>
                    <div class="caption grey--text mb-1">Absent to</div>
                    <DxDateBox
                      type="time"
                      picker-type="list"
                      :interval="15"
                      :width="150"
                      :value="asTimeValue(form.timeTo)"
                      :show-clear-button="true"
                      @value-changed="onTimeChanged('timeTo', $event)"
                    />
                  </div>
                </v-layout>

                <v-btn
                  small
                  outline
                  color="success"
                  class="ma-0 mt-3"
                  :loading="saving"
                  @click="save"
                >
                  Save Deduction
                </v-btn>
              </template>
            </v-card>
          </v-flex>

          <v-flex xs12 sm6 class="pl-3">
            <div class="font-weight-medium mb-2">
              <v-icon small color="grey darken-1" class="mr-1">mdi-format-list-bulleted</v-icon>
              Absences this day
              <v-chip small class="ml-1 absences-count-chip">{{ absences.length }}</v-chip>
            </div>
            <div v-if="!absences.length" class="grey--text caption">
              No absence rows for this deduction.
            </div>
            <template v-for="(group, gIndex) in groupedAbsences">
              <div :key="'h-' + group.name" class="absence-group-header">
                {{ group.name }}
                <span class="grey--text">({{ group.rows.length }})</span>
              </div>
              <div v-for="row in group.rows" :key="row.id" class="absence-grid-row">
                <div class="absence-grid-main">
                  <div class="absence-grid-cell absence-grid-time">
                    {{ absenceTimeLabel(row) }}
                  </div>
                  <div v-if="row.routeIdentifier" class="absence-grid-cell absence-grid-route">
                    {{ row.routeIdentifier }}
                  </div>
                  <div class="absence-grid-cell absence-grid-reason grey--text">
                    {{ row.absentReason || "-" }}
                  </div>
                </div>
                <div v-if="row.note" class="absence-grid-note">{{ row.note }}</div>
              </div>
              <v-divider v-if="gIndex + 1 < groupedAbsences.length" :key="'d-' + group.name"></v-divider>
            </template>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import DxDateBox from "devextreme-vue/date-box";

export default {
  components: {
    DxDateBox,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    // { driverBusID, driver, busNumber, date, kind, timeFrom, timeTo }
    row: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      absences: [],
      preview: null,
      form: this.emptyForm(),
    };
  },
  computed: {
    title() {
      if (!this.row) return "";
      const bus = this.row.busNumber ? "#" + this.row.busNumber + " " : "";
      return bus + (this.row.driver || "Driver") + " — " + this.formatDate(this.row.date);
    },
    groupedAbsences() {
      if (!this.absences.length) return [];
      const order = ["AM", "PM", "CUSTOM", "FAM", "PPM"];
      const groups = {};
      this.absences.forEach((row) => {
        const name = row.groupBy || "Other";
        if (!groups[name]) groups[name] = [];
        groups[name].push(row);
      });
      return Object.keys(groups)
        .sort((a, b) => {
          const ai = order.indexOf(a);
          const bi = order.indexOf(b);
          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        })
        .map((name) => ({ name: name, rows: groups[name] }));
    },
  },
  watch: {
    value(open) {
      if (open) this.load();
    },
    row() {
      if (this.value) this.load();
    },
  },
  methods: {
    emptyForm() {
      return {
        selectedPreset: "",
        noDeductReason: "",
        customAmount: "",
        customReason: "",
        timeFrom: null,
        timeTo: null,
      };
    },
    money(value) {
      return Number(value || 0).toFixed(2);
    },
    formatDate(value) {
      return value ? moment(value).format("M/D/YYYY") : "";
    },
    absenceTimeLabel(row) {
      if (row.timeStart) return row.timeStart;
      return row.routeIdentifier ? "No time" : "No route";
    },
    categoryLabel(category) {
      if (category === "AM+CUSTOM") return "AM + CUSTOM";
      if (category === "PM+CUSTOM") return "PM + CUSTOM";
      return category || "";
    },
    presetLabel(preset) {
      const map = {
        DAY: "Full day",
        AM: "AM",
        PM: "PM",
        TRIP: "Trip",
        ROUTES: "By routes",
        DURATION: "By duration",
        TIME: "By missing time",
        NO_DEDUCT: "No deduct",
        MANUAL: "Custom amount",
      };
      return map[preset] || preset || "—";
    },
    asTimeValue(value) {
      if (!value) return null;
      if (moment.isDate(value)) {
        return moment(value).isValid() ? value : null;
      }
      const parsed = moment(value, ["h:mm A", "HH:mm", "HH:mm:ss"], true);
      return parsed.isValid() ? parsed.toDate() : null;
    },
    parseTime(value) {
      if (!value) return null;
      const parsed = moment.isDate(value)
        ? moment(value)
        : moment(value, ["h:mm A", "HH:mm", "HH:mm:ss"], true);
      return parsed.isValid() ? parsed.format("HH:mm:ss") : null;
    },
    onTimeChanged(field, event) {
      const value = event && event.value !== undefined ? event.value : null;
      const next = this.asTimeValue(value);
      if (!next && !(event && event.event)) return;
      if (this.parseTime(this.form[field]) === this.parseTime(next)) {
        return;
      }
      this.form[field] = next;
    },
    // Loads both the absences behind the deduction and a live preview, so the amount and
    // category reflect the current state rather than a stale snapshot.
    async load() {
      const row = this.row;
      if (!row) return;
      this.loading = true;
      this.absences = [];
      this.preview = null;
      try {
        const dateStr = moment(row.date).format("YYYY-MM-DD");
        const [absences, preview] = await Promise.all([
          this.$http.get(
            "api/driverAbsent/getAbsencesForDeduction?driverBusId=" +
              row.driverBusID +
              "&date=" +
              dateStr +
              "&kind=" +
              encodeURIComponent(row.kind || "")
          ),
          this.$http.post("api/driverAbsent/previewDeduction", {
            driverBusID: row.driverBusID,
            date: row.date,
            kind: row.kind,
            timeFrom: row.timeFrom,
            timeTo: row.timeTo,
          }),
        ]);
        this.absences = absences || [];
        this.preview = preview;

        const existing = preview.existing;
        this.form = {
          selectedPreset:
            (existing && existing.preset) || preview.recommendedPreset || "NO_DEDUCT",
          noDeductReason: (existing && existing.noDeductReason) || "",
          customAmount: existing && existing.preset === "MANUAL" ? existing.amount : "",
          customReason:
            existing && existing.preset === "MANUAL" ? existing.noDeductReason || "" : "",
          timeFrom: (existing && existing.timeFrom) || row.timeFrom,
          timeTo: (existing && existing.timeTo) || row.timeTo,
        };
      } catch (e) {
        this.absences = [];
        this.preview = null;
      } finally {
        this.loading = false;
      }
    },
    formError() {
      if (!this.preview || !this.form.selectedPreset) return "";
      if (this.form.selectedPreset === "NO_DEDUCT" && !this.form.noDeductReason) {
        return "A reason is required when not deducting";
      }
      if (
        this.form.selectedPreset === "MANUAL" &&
        (this.form.customAmount === "" ||
          this.form.customAmount == null ||
          isNaN(Number(this.form.customAmount)))
      ) {
        return "Enter the custom amount";
      }
      return "";
    },
    payload() {
      if (!this.row || !this.preview || !this.form.selectedPreset) return null;
      const suggestion = (this.preview.suggestions || []).find(
        (s) => s.preset === this.form.selectedPreset
      );
      const amount =
        this.form.selectedPreset === "MANUAL"
          ? Number(this.form.customAmount)
          : suggestion
          ? suggestion.amount
          : this.preview.recommendedAmount;

      return {
        driverBusID: this.row.driverBusID,
        date: this.row.date,
        kind: this.row.kind,
        category: this.preview.category,
        preset: this.form.selectedPreset,
        amount: amount,
        noDeductReason:
          this.form.selectedPreset === "MANUAL"
            ? this.form.customReason
            : this.form.noDeductReason,
        timeFrom: this.parseTime(this.form.timeFrom),
        timeTo: this.parseTime(this.form.timeTo),
        updateOutWindow: true,
        enteredBy: this.$store.state.authentication.user,
      };
    },
    async save() {
      const error = this.formError();
      if (error) {
        return this.$notify({ text: error, group: "notification", type: "error" });
      }
      const payload = this.payload();
      if (!payload) return;

      try {
        this.saving = true;
        await this.$http.post("api/driverAbsent/saveDeduction", payload);
        this.$notify({
          group: "notification",
          text: "Deduction saved",
          type: "success",
        });
        await this.load();
        this.$emit("saved");
      } catch (e) {
        this.$notify({
          group: "notification",
          text: (e && e.body) || "Failed to save deduction",
          type: "error",
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.deduction-info-card.v-card {
  background-color: #fff8f0 !important;
  border: 1px solid #ffe0b2 !important;
  border-left: 4px solid #fb8c00 !important;
  border-radius: 6px !important;
  box-shadow: none !important;
}
.current-deduction-box {
  background-color: #fff;
  border: 1px solid #ffe0b2;
  border-radius: 4px;
  padding: 8px 10px;
}
.no-deduction-banner {
  display: flex;
  align-items: center;
  background-color: #ffebee;
  border: 1px solid #ef9a9a;
  border-left: 4px solid #c62828;
  border-radius: 4px;
  padding: 8px 10px;
  color: #c62828;
  font-weight: 600;
}
.absences-count-chip.v-chip {
  background-color: #eeeeee;
  height: 20px;
}
.absence-group-header {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 600;
  color: #757575;
  margin: 10px 0 4px;
}
.absence-group-header:first-child {
  margin-top: 4px;
}
.absence-grid-row {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 4px 0;
  font-size: 13px;
  border-bottom: 1px solid #f5f5f5;
}
.absence-grid-row:last-child {
  border-bottom: none;
}
.absence-grid-main {
  display: flex;
  align-items: baseline;
}
.absence-grid-note {
  margin: 1px 0 2px 90px;
  padding-left: 8px;
  border-left: 2px solid #cfd8dc;
  font-size: 12px;
  color: #455a64;
  line-height: 1.3;
}
.absence-grid-cell {
  padding-right: 10px;
}
.absence-grid-time {
  flex: 0 0 90px;
  font-weight: 500;
}
.absence-grid-route {
  flex: 1 1 auto;
}
.absence-grid-reason {
  flex: 0 0 auto;
  text-align: right;
  max-width: 40%;
}
</style>
